/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useCountryContext } from "../../../context/CountryContext";
import {
  Select,
  TextArea,
  MyMessage,
  InputField,
  SelectInput,
  MyButton,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import {
  getAddLevel,
  getEditLevel,
} from "../../../lib/sysNavigation/SysNavigation";
import { requireFields } from "../../../lib/validators";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { FOLDERS, image_kit_folder } from "../../doseeData/utils/Utils";
import { useBaseLevelContext } from "../../../context/levels/BaseLevelContext";
import GenerateDescription from "../../doseeData/utils/GenerateDescription";
import { FaHandSparkles } from "react-icons/fa";
import {
  FiLayers,
  FiImage,
  FiAlignLeft,
  FiShield,
  FiInfo,
} from "react-icons/fi";

const levelsRoute = "/levels";

const Edit = () => {
  const { update } = useBaseLevelContext();
  const level = getEditLevel();
  const { countries, fetchCountries } = useCountryContext();

  const labelRaw = getAddLevel() ? getAddLevel() : "First";
  const label = String(labelRaw);
  const normalizedLabel = label.toLowerCase();

  const countryId = level?.country_id;

  const [isCity, setIsCity] = useState<any>(level?.is_city);
  const [thumbnail, setThumbnail] = useState<any>(level?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(level?.image_cdn_id);

  const [gdp, set_gdp] = useState<any>(level?.gdp);
  const [levelName, set_levelName] = useState<any>(level?.name);
  const [population, set_population] = useState<any>(level?.population);
  const [description, set_description] = useState<any>(level?.description);

  const [tourism, set_tourism] = useState<any>(level?.tourism);
  const [safety_indicator, set_safety_indicator] = useState<any>(
    level?.safety_indicator,
  );

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  useEffect(() => {
    if (!countries) fetchCountries();
  }, [countries, fetchCountries]);

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const sendAddCountryRequest = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    const validation = requireFields({
      GDP: gdp,
      Name: levelName,
      Population: population,
      Tourism: tourism,
      "Safety Indicator": safety_indicator,
      Description: description,
    });

    if (validation.isError) {
      setLoading(false);
      return responses(validation.isError, validation.message);
    }
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, "Image Required");
    }
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const dataObject: Record<string, any> = {
      country_id: countryId,
      name: levelName,
      slug: levelName.toLowerCase().replace(/\s/g, "_"),
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "x",
      description: description,
      population: population,
      is_city: isCity,
      main_city: levelName,
      gdp: gdp,
      tourism: tourism,
      safety_indicator: safety_indicator,
      status: 1,
    };

    if (normalizedLabel === "first") {
      dataObject.first_level_label = levelName;
    } else if (normalizedLabel === "second") {
      dataObject.first_level_id = level?.first_level_id;
      dataObject.second_level_label = levelName;
    } else if (normalizedLabel === "third") {
      dataObject.first_level_id = level?.first_level_id;
      dataObject.second_level_id = level?.second_level_id;
      dataObject.third_level_label = levelName;
    } else if (normalizedLabel === "fourth") {
      dataObject.first_level_id = level?.first_level_id;
      dataObject.second_level_id = level?.second_level_id;
      dataObject.third_level_id = level?.third_level_id;
      dataObject.fourth_level_label = levelName;
    }

    const id =
      normalizedLabel === "first"
        ? level?.first_level_id
        : normalizedLabel === "second"
          ? level?.second_level_id
          : normalizedLabel === "third"
            ? level?.third_level_id
            : level?.fourth_level_id;

    setLoading(true);
    const { message: msg, status } = await update({
      level_id: id,
      data: dataObject,
      level: normalizedLabel,
    });
    setLoading(false);

    if (status === 200 || status === 201) {
      return responses(false, msg);
    } else {
      return responses(true, msg);
    }
  };

  const countryName =
    countries?.find((c: any) => c?.country_id === countryId)?.name || "Levels";

  return (
    <AddPageShell
      backRoute={`${levelsRoute}/${normalizedLabel}`}
      eyebrow={`Geography • ${label} Level`}
      title={`Edit ${label} Level`}
      subtitle="Updated hierarchy, identity, media and safety information for this level."
      icon={<FiLayers className="h-5 w-5" />}
      pills={[
        { label: "Level", value: label },
        { label: "Country", value: countryName || "—" },
      ]}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={set_description}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={sendAddCountryRequest}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Media"
        hint="Upload a representative image for this level."
        icon={<FiImage className="h-4 w-4" />}
      >
        {countries && countryId ? (
          <ImageKitUpload
            thumbnail={thumbnail}
            thumbnailId={thumbnailId}
            setThumbnail={setThumbnail}
            setThumbnailId={setThumbnailId}
            folder={image_kit_folder(countryName, FOLDERS.LEVELS)}
          />
        ) : (
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
            <FiInfo className="h-4 w-4" />
            Country not found. Please ensure this level is linked to a country.
          </div>
        )}
      </AddSection>

      <AddSection
        title="Level Details"
        hint="Identity, classification and key indicators."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-4">
          <InputField
            props={{
              id: "levelName",
              title: `${label} Level Name`,
              type: "text",
              required: true,
              value: levelName ? levelName : "",
              onChange: (e: any) => {
                set_levelName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <SelectInput
            defaultValue={isCity && isCity === 1 ? "yes" : "no"}
            options={["no", "yes"]}
            title={"Is it a City?"}
            onChange={(e: any) => {
              if (e.target.selectedIndex === 0) setIsCity(0);
              else setIsCity(1);
              setError(null);
              setSuccess(null);
            }}
          />
          <InputField
            props={{
              id: "population",
              title: "Population",
              type: "number",
              required: true,
              value: population ? population : "",
              onChange: (e: any) => {
                set_population(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "gdp",
              title: "GDP",
              type: "number",
              required: true,
              value: gdp ? gdp : "",
              onChange: (e: any) => {
                set_gdp(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="A descriptive overview used on listings and detail pages."
        icon={<FiAlignLeft className="h-4 w-4" />}
        actions={
          <MyButton
            onClick={() => setShowDescriptionModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3 py-2 text-xs font-semibold text-white shadow hover:opacity-95"
          >
            <FaHandSparkles className="h-4 w-4" />
            Generate
          </MyButton>
        }
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            value: description ? description : "",
            onChange: (e: any) => {
              set_description(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>

      <AddSection
        title="Safety & Security"
        hint="How travellers should perceive this destination."
        icon={<FiShield className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Select
            props={{
              id: "tourism",
              title: "Tourism",
              required: true,
              option: ["safe", "intermediate", "danger"],
              preselect:
                tourism !== null && tourism !== undefined
                  ? [{ name: tourism }]
                  : null,
              onChange: (e: any) => {
                set_tourism(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "safety_indicator",
              title: "Safety Indicator",
              required: true,
              option: ["safe", "intermediate", "danger"],
              preselect:
                safety_indicator !== null && safety_indicator !== undefined
                  ? [{ name: safety_indicator }]
                  : null,
              onChange: (e: any) => {
                set_safety_indicator(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>
    </AddPageShell>
  );
};

export default Edit;
